/**
 * @documental
 * @export LoadingGroup
 * @import
 * import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
 */

import { FC, useState, useEffect, useMemo, PropsWithChildren } from "react";
import { withSubComponents } from "../../utilities/withSubComponents";
import { LoadingGroupContext, LoadingGroupContextValue } from "./LoadingGroupContext";
import { Placeholder } from "./Placeholder";
import { useLoading } from "./useLoading";

/**
 * The `LoadingGroup` component properties.
 */
export interface LoadingGroupProps {

  /**
   * The loading state. This can either be true/false or a Promise.
   * If a Promise is specified, the loading group will be in a loading
   * state until the promise is resolved.
   */
  readonly loading?: boolean | Promise<unknown>;
}

/**
 * The `LoadingGroup` component sub-components.
 */
export interface LoadingGroupSubComponents {

  /**
   * A component which renders fallback content instead of its children if loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * A component which informs children whether a loading operation is taking place.
 */
export const LoadingGroup = withSubComponents<FC<PropsWithChildren<LoadingGroupProps>>, LoadingGroupSubComponents>({
  Placeholder,
}, (props) => {

  const { loading = false, children } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const parentLoading = useLoading();

  useEffect(() => {
    if (typeof loading === "boolean") {
      setIsLoading(loading);
      return;
    }

    setIsLoading(true);
    loading.finally(() => setIsLoading(false));
  }, [loading]);

  const contextValue = useMemo((): LoadingGroupContextValue => ({
    loading: isLoading || parentLoading,
  }), [isLoading, parentLoading]);

  return (
    <LoadingGroupContext.Provider value={contextValue}>
      {children}
    </LoadingGroupContext.Provider>
  );
});
